import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import {
  REMINDER_UNITS,
  describeReminderLead,
  getApiErrorMessage,
  reminderMinutes,
  validateReminderMinutes,
} from "./meetingUtils"

// When reminder emails go out before a booked meeting. Each one is sent to the
// meeting's organizer and to the candidate.
export default function MeetingRemindersSection() {
  const [reminders, setReminders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [amount, setAmount] = useState("15")
  const [unit, setUnit] = useState("minutes")
  const [isAdding, setIsAdding] = useState(false)

  const load = useCallback(() => {
    setIsLoading(true)
    return adminApi
      .fetchMeetingReminders()
      .then(({ data }) => setReminders(data.results || []))
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not load the reminders.")
        )
      )
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const minutes = reminderMinutes(amount, unit)
  const amountError = amount === "" ? "" : validateReminderMinutes(minutes)

  function handleAdd(event) {
    event.preventDefault()
    setErrorMessage("")
    setIsAdding(true)
    adminApi
      .createMeetingReminder({ minutes_before: minutes })
      .then(load)
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not add the reminder.")
        )
      )
      .finally(() => setIsAdding(false))
  }

  function handleToggle(reminder) {
    setErrorMessage("")
    adminApi
      .updateMeetingReminder(reminder.id, { is_active: !reminder.is_active })
      .then(load)
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not update the reminder.")
        )
      )
  }

  function handleDelete(reminder) {
    setErrorMessage("")
    adminApi
      .deleteMeetingReminder(reminder.id)
      .then(load)
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not remove the reminder.")
        )
      )
  }

  return (
    <Column gap={2}>
      <Typography variant="body2" color="text.secondary">
        A reminder email goes out this long before each meeting booked in
        OneBase. It is sent to both the meeting organizer (the user) and the
        candidate. With none set up, no reminder emails are sent.
      </Typography>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <form onSubmit={handleAdd}>
          <Row sx={{ gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            <TextField
              label="Send reminder"
              type="number"
              size="small"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputProps={{ min: 1 }}
              error={Boolean(amountError)}
              helperText={amountError || undefined}
              sx={{ width: 140 }}
            />
            <TextField
              select
              label="Unit"
              size="small"
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
              sx={{ minWidth: 140 }}
            >
              {REMINDER_UNITS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" sx={{ pt: 1 }}>
              before the meeting
            </Typography>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isAdding}
              disabled={amount === "" || Boolean(amountError)}
            >
              Add reminder
            </LoadingButton>
          </Row>
        </form>
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Sent to</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reminders.map((reminder) => (
              <TableRow key={reminder.id}>
                <TableCell>
                  {describeReminderLead(reminder.minutes_before)} before
                </TableCell>
                <TableCell>Organizer and candidate</TableCell>
                <TableCell>
                  <Switch
                    checked={reminder.is_active}
                    onChange={() => handleToggle(reminder)}
                    inputProps={{
                      "aria-label": `Send ${describeReminderLead(
                        reminder.minutes_before
                      )} reminder`,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={`Remove ${describeReminderLead(
                      reminder.minutes_before
                    )} reminder`}
                    onClick={() => handleDelete(reminder)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && reminders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No reminders yet. Add one above, for example 15 minutes
                    before.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Column>
  )
}
